import { clsx } from 'clsx';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  /** Classes for the wrapping inline element. */
  className?: string;
  /** Delay before showing on hover (ms). Focus shows immediately. */
  delay?: number;
};

type Position = { top: number; left: number; below: boolean };

/**
 * Lightweight tooltip on hover/focus of its children (wrapped in an inline span). The bubble is portaled and
 * fixed-positioned so it is never clipped. Content is supplementary — never put essential info only here.
 */
export function Tooltip({ content, children, className, delay = 300 }: TooltipProps) {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [position, setPosition] = useState<Position | null>(null);
  const id = useId();

  useEffect(() => {
    const pending = timer;
    return () => window.clearTimeout(pending.current);
  }, []);

  const show = (immediate: boolean) => {
    window.clearTimeout(timer.current);
    const reveal = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const below = rect.top < 40;
      setPosition({ top: below ? rect.bottom + 6 : rect.top - 6, left: rect.left + rect.width / 2, below });
    };
    if (immediate) reveal();
    else timer.current = window.setTimeout(reveal, delay);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setPosition(null);
  };

  if (content === null || content === undefined || content === '') return <>{children}</>;

  return (
    <span
      ref={anchorRef}
      className={clsx('inline-flex max-w-full', className)}
      onMouseEnter={() => show(false)}
      onMouseLeave={hide}
      onFocus={() => show(true)}
      onBlur={hide}
      onKeyDown={(e) => e.key === 'Escape' && hide()}
      aria-describedby={position ? id : undefined}
    >
      {children}
      {position &&
        createPortal(
          <span
            id={id}
            role="tooltip"
            style={{ position: 'fixed', top: position.top, left: position.left }}
            className={clsx(
              'pointer-events-none z-[70] max-w-72 -translate-x-1/2 animate-fade-in rounded-md bg-slate-900 px-2 py-1 text-xs leading-snug text-white shadow-popover dark:bg-slate-300 dark:text-slate-900',
              !position.below && '-translate-y-full',
            )}
          >
            {content}
          </span>,
          document.body,
        )}
    </span>
  );
}
